import { Injectable } from '@angular/core';
import { gTDB } from './db';

interface ClockAnchor {
  serverMs: number;
  perfMs: number;
}

interface SavedClock {
  skewMs: number;
  syncedAtClientMs: number;
  syncedServerMs: number;
}

export interface ClientTimestamp {
  updated_at: number; // Use this for updated_at / sync timestamps.
  device_time_ms: number; // actual device time for debugging
}

@Injectable({
  providedIn: 'root',
})
export class SyncClockService {
  private readonly CLOCK_KEY = 'serverSyncedClock';

  private readonly RESYNC_INTERVAL_MS = 1 * 60 * 1000; //  1 minute

  private readonly CLOCK_DRIFT_WARNING_MS = 30 * 1000;

  private anchor: ClockAnchor = {
    serverMs: Date.now(),
    perfMs: performance.now(),
  };

  private currentSkewMs = 0;

  private lastSyncPerfMs = 0;

  private syncInProgress = false;

  private initialized = false;

  async init(): Promise<void> {
    const saved = (await gTDB.userData.get(this.CLOCK_KEY)) as
      SavedClock | undefined;

    this.currentSkewMs = saved?.skewMs ?? 0;

    // If offline this allows us to continue approximately from the last known server clock.
    this.anchor = {
      serverMs: Date.now() + this.currentSkewMs,

      perfMs: performance.now(),
    };

    this.initialized = true;

    if (navigator.onLine) {
      this.sync().catch(() => {});
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        this.maybeSync().catch(() => {});
      }
    });

    window.addEventListener('online', () => {
      this.sync().catch(() => {});
    });
  }

  // Get estimated server UTC time in milliseconds. This uses performance.now(), NOT Date.now(),once the clock has been initialised.

  nowMs(): number {
    return this.anchor.serverMs + (performance.now() - this.anchor.perfMs);
  }

  timestamp(): ClientTimestamp {
    return {
      updated_at: this.nowMs(),
      device_time_ms: Date.now(), // debug only value
    };
  }

  // sync with backend call health api for minimal return time
  async sync(): Promise<void> {
    if (!navigator.onLine) {
      return;
    }
    if (this.syncInProgress) {
      return;
    }

    this.syncInProgress = true;

    try {
      let best: {
        serverMs: number;
        perfMs: number;
        rttMs: number;
      } | null = null;

      for (let i = 0; i < 3; i++) {
        const p0 = performance.now();

        const serverMs = await this.fetchServerMs();

        const p1 = performance.now();

        const rttMs = p1 - p0;

        // Estimate the point in performance.now()
        // corresponding to the server timestamp.
        const perfMs = p0 + rttMs / 2;

        if (!best || rttMs < best.rttMs) {
          best = {
            serverMs,
            perfMs,
            rttMs,
          };
        }
      }

      if (!best) {
        throw new Error('Unable to synchronise clock');
      }

      this.anchor = {
        serverMs: best.serverMs,
        perfMs: best.perfMs,
      };

      this.currentSkewMs = this.nowMs() - Date.now();

      this.lastSyncPerfMs = performance.now();

      const saved: SavedClock = {
        skewMs: this.currentSkewMs,
        syncedAtClientMs: Date.now(),
        syncedServerMs: this.nowMs(),
      };
      await gTDB.userData.put(saved, this.CLOCK_KEY);
    } finally {
      this.syncInProgress = false;
    }
  }

  // sync only if needed
  async maybeSync(resyncInterval = this.RESYNC_INTERVAL_MS): Promise<void> {
    const nowPerfMs = performance.now();

    if (
      this.lastSyncPerfMs !== 0 &&
      nowPerfMs - this.lastSyncPerfMs < resyncInterval
    ) {
      return;
    }

    await this.sync();
  }

  private async fetchServerMs(): Promise<number> {
    const url = `/api/shared/health/?ngsw-bypass=true&_=${Date.now()}`;

    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Clock sync failed: HTTP ${response.status}`);
    }

    const data = await response.json();

    if (data?.status != 'success' || !data?.server_ms) {
      throw new Error(`Clock sync failed`);
    }

    if (!Number.isFinite(data.server_ms)) {
      throw new Error('Invalid server time');
    }

    return data.server_ms;
  }

  getStatus() {
    const estimatedServerMs = this.nowMs();

    const deviceWallMs = Date.now();

    const estimatedFromWallMs = deviceWallMs + this.currentSkewMs;

    const differenceMs = estimatedFromWallMs - estimatedServerMs;

    return {
      initialized: this.initialized,

      estimatedServerMs,

      deviceWallMs,

      differenceMs,

      wallClockChangedSignificantly:
        Math.abs(differenceMs) > this.CLOCK_DRIFT_WARNING_MS,

      online: navigator.onLine,
    };
  }
}
